/**
 * Fantasy Console 0 - Auth Manager Component
 *
 * Manages authentication modals and provides methods to open them.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { SignInModal } from '../Modals/SignInModal'
import { SignUpModal } from '../Modals/SignUpModal'

interface AuthManagerContextType {
  openSignIn: () => void
  openSignUp: () => void
  closeAll: () => void
}

const AuthManagerContext = createContext<AuthManagerContextType | null>(null)

export function AuthManager({ children }: { children: ReactNode }) {
  const [showSignIn, setShowSignIn] = useState(false)
  const [showSignUp, setShowSignUp] = useState(false)

  const openSignIn = useCallback(() => {
    setShowSignUp(false)
    setShowSignIn(true)
  }, [])

  const openSignUp = useCallback(() => {
    setShowSignIn(false)
    setShowSignUp(true)
  }, [])

  const closeAll = useCallback(() => {
    setShowSignIn(false)
    setShowSignUp(false)
  }, [])

  const switchToSignUp = useCallback(() => {
    setShowSignIn(false)
    setShowSignUp(true)
  }, [])

  const switchToSignIn = useCallback(() => {
    setShowSignUp(false)
    setShowSignIn(true)
  }, [])

  return (
    <AuthManagerContext.Provider value={{ openSignIn, openSignUp, closeAll }}>
      {children}

      <SignInModal
        isOpen={showSignIn}
        onClose={closeAll}
        onSwitchToSignUp={switchToSignUp}
      />

      <SignUpModal
        isOpen={showSignUp}
        onClose={closeAll}
        onSwitchToSignIn={switchToSignIn}
      />
    </AuthManagerContext.Provider>
  )
}

export function useAuthManager(): AuthManagerContextType {
  const context = useContext(AuthManagerContext)
  if (!context) {
    throw new Error('useAuthManager must be used within an AuthManager')
  }
  return context
}
